import { gql } from '@apollo/client';

/**
 * GraphQL documents for the Releases tool, kept out of the scene so the
 * component file reads as behavior rather than a wall of query text.
 */

const RELEASE_PREVIEW_FIELDS = gql`
  fragment ReleasePreviewFields on ReleasePreview {
    baseTag
    baseSha
    headSha
    expectedHeadSha
    computedNextTag
    summary
    prerequisites {
      text
      prNumbers
    }
    riskCallouts
    testPlan {
      action
      expectedResult
    }
    ciStatus {
      conclusion
      totalCount
      successCount
      failureCount
      cancelledCount
      pendingCount
    }
    commits {
      sha
      shortSha
      subject
      author
      authoredAt
      prNumber
      prUrl
      ciConclusion
    }
  }
`;

export const ADMIN_START_RELEASE_PREVIEW = gql`
  mutation AdminStartReleasePreview($headSha: String) {
    adminStartReleasePreview(headSha: $headSha) {
      status
      baseSha
      headSha
      preview {
        ...ReleasePreviewFields
      }
    }
  }
  ${RELEASE_PREVIEW_FIELDS}
`;

export const ADMIN_GET_RELEASE_PREVIEW = gql`
  query AdminGetReleasePreview($baseSha: String!, $headSha: String!) {
    adminGetReleasePreview(baseSha: $baseSha, headSha: $headSha) {
      status
      baseSha
      headSha
      preview {
        ...ReleasePreviewFields
      }
    }
  }
  ${RELEASE_PREVIEW_FIELDS}
`;

export const ADMIN_GET_RELEASE_CHANGE_LOG = gql`
  query AdminGetReleaseChangeLog {
    adminGetReleaseChangeLog {
      baseTag
      baseSha
      headSha
      computedNextTag
      releasableHeadSha
      ciStatus {
        conclusion
        totalCount
        successCount
        failureCount
        cancelledCount
        pendingCount
      }
      commits {
        sha
        shortSha
        subject
        author
        authoredAt
        prNumber
        prUrl
        ciConclusion
      }
    }
  }
`;

export const ADMIN_GET_CURRENT_RELEASE_DRAFT = gql`
  query AdminGetCurrentReleaseDraft {
    adminGetCurrentReleaseDraft {
      tag
      targetSha
      htmlUrl
      draftedByEmail
      draftedAt
      body
      prerequisiteCheckedIndices
      testPlanCheckedIndices
      prerequisiteLineContents
      testPlanLineContents
      prerequisiteCheckedMarkers
      testPlanCheckedMarkers
    }
  }
`;

export const ADMIN_GET_ASSISTANT_BENCHMARK_FOR_HEAD = gql`
  query AdminGetAssistantBenchmarkForHead($headSha: String!) {
    adminGetAssistantBenchmarkForHead(headSha: $headSha) {
      id
      baseSha
      headSha
      status
      githubRunUrl
      verdict
    }
  }
`;

export const ADMIN_LIST_RELEASES = gql`
  query AdminListReleases {
    adminListReleases {
      tag
      sha
      body
      mopsAuthorEmail
      githubAuthorLogin
      createdAt
    }
  }
`;

export const ADMIN_ANNOUNCE_PRE_RELEASE = gql`
  mutation AdminAnnouncePreRelease($expectedHeadSha: String!) {
    adminAnnouncePreRelease(expectedHeadSha: $expectedHeadSha) {
      draftTag
      draftUrl
      slackMessageUrl
    }
  }
`;

export const ADMIN_PUBLISH_RELEASE_DRAFT = gql`
  mutation AdminPublishReleaseDraft($expectedDraftTag: String!) {
    adminPublishReleaseDraft(expectedDraftTag: $expectedDraftTag) {
      tag
      sha
      createdAt
    }
  }
`;

export const ADMIN_DELETE_RELEASE_DRAFT = gql`
  mutation AdminDeleteReleaseDraft($expectedDraftTag: String!) {
    adminDeleteReleaseDraft(expectedDraftTag: $expectedDraftTag) {
      deletedTag
    }
  }
`;

export const ADMIN_TOGGLE_RELEASE_CHECKLIST_ITEM = gql`
  mutation AdminToggleReleaseChecklistItem(
    $expectedDraftTag: String!
    $section: ChecklistSection!
    $index: Int!
    $checked: Boolean!
    $expectedLineHash: String!
  ) {
    adminToggleReleaseChecklistItem(
      expectedDraftTag: $expectedDraftTag
      section: $section
      index: $index
      checked: $checked
      expectedLineHash: $expectedLineHash
    ) {
      tag
      targetSha
      htmlUrl
      draftedByEmail
      draftedAt
      body
      prerequisiteCheckedIndices
      testPlanCheckedIndices
      prerequisiteLineContents
      testPlanLineContents
      prerequisiteCheckedMarkers
      testPlanCheckedMarkers
    }
  }
`;
